import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  StatusBadge,
  useToast,
  v,
} from "@kss/ui";
import { Camera, KeyRound, RefreshCw, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { updateProfile, useAuth } from '@kss/auth';
import { authService, userApiService, type ApiUser } from '@kss/api';
import { persistImage } from '../../utils/persistImage';

// 20 KB cap mirrors the customer-facing AccountProfile so the persisted
// avatar payload stays well under the localStorage 5 MB ceiling.
const MAX_AVATAR_BYTES = 20 * 1024;

/**
 * Admin user profile page (`/panel/profile`).
 *
 * Reads the current signed-in user from the auth slice, fetches the full
 * `ApiUser` record from `/api/User/Get`, and lets the admin edit the
 * editable subset (first / last name, email, phone). Identity fields
 * (userName, role, tenant) and audit metadata are read-only.
 *
 * Avatar upload is currently client-side only — the backend `User`
 * table has no `imageUrl` column. The captured base64 lives in the
 * persisted auth slice. See `BACKEND_AGENT_PROMPTS.md` for the schema
 * change needed to make this server-backed.
 *
 * Password change is also a TODO — there's no `POST /auth/change-password`
 * endpoint on the live API. The button shows a "Not yet available" toast
 * so admins know it's intentional, not broken.
 */
export default function UserProfile() {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const fileRef = useRef<HTMLInputElement>(null);

  const [record, setRecord] = useState<ApiUser | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Names use `nameChars` rather than the generic text guards — a
     person's name has no legitimate reason to contain digits or
     punctuation beyond hyphen/apostrophe, and the allowlist fails
     closed where a blocklist would not. */
  const profileErrors: Record<string, string | null> = {
    firstName: v.runRules(firstName, [
      v.required('First name'),
      v.maxLength(80, 'First name'),
      v.nameChars('First name'),
    ]),
    lastName: v.runRules(lastName, [v.maxLength(80, 'Last name'), v.nameChars('Last name')]),
    email: v.runRules(email, [v.required('Email'), v.email()]),
    phone: v.runRules(phone, [v.mobile({ label: 'Phone' })]),
  };
  const profErr = (k: string) => (touched[k] ? profileErrors[k] ?? undefined : undefined);
  const markP = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const profileValid = !Object.values(profileErrors).some(Boolean);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const rec = await userApiService.get(userId);
      setRecord(rec);
      setFirstName(rec.firstName ?? '');
      setLastName(rec.lastName ?? '');
      setEmail(rec.email ?? '');
      setPhone(rec.phone ?? '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load profile';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const initial =
    (firstName?.[0] || user?.name?.[0] || '?').toUpperCase();

  function pickAvatar() {
    fileRef.current?.click();
  }

  /* Avatar upload — was previously persisting a base64 data URL to
     the redux-persist auth slice, which meant the avatar lived only
     in the user's local browser. Now:
       1. Read the file → data URL.
       2. `persistImage(dataUrl, 'user')` → POSTs to `/api/Image/upload`,
          returns a hosted `/uploads/{tenantId}/user/.../guid.png` URL.
       3. `userApiService.update([{ userId, imageUrl }])` persists the
          hosted URL on the User row so it survives a fresh device.
       4. Mirror the URL into the redux-persist auth slice so the
          panel's topbar avatar updates immediately.
     If steps 2-3 fail (backend hasn't shipped the `imageUrl` column
     yet — Item 6.4) we fall back to the data URL, matching the
     previous behaviour, and surface the server's error to a toast. */
  async function onAvatarFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Invalid file', 'Pick an image (PNG, JPG, WebP).');
      return;
    }
    if (f.size > MAX_AVATAR_BYTES) {
      toast.error(
        'Image too large',
        `Max ${Math.round(MAX_AVATAR_BYTES / 1024)} KB. Crop or compress.`,
      );
      return;
    }
    let dataUrl = '';
    try {
      dataUrl = await new Promise<string>((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(String(reader.result ?? ''));
        reader.onerror = () => rej(new Error('Read failed'));
        reader.readAsDataURL(f);
      });
    } catch (err) {
      toast.error('Read failed', err instanceof Error ? err.message : 'Read failed');
    }
    if (!dataUrl) return;

    try {
      const hostedUrl = await persistImage(dataUrl, 'user');
      if (record?.userId && hostedUrl) {
        await userApiService.update([
          { ...record, userId: record.userId, imageUrl: hostedUrl } as ApiUser,
        ]);
      }
      dispatch(updateProfile({ avatar: hostedUrl ?? dataUrl }));
      toast.success('Avatar updated', f.name);
    } catch (err) {
      // Fall back to local-only persistence so the avatar at least
      // shows in the current session; the server message is surfaced.
      dispatch(updateProfile({ avatar: dataUrl }));
      toast.error(
        'Avatar saved locally only',
        err instanceof Error ? err.message : 'Server upload failed.',
      );
    }
  }

  async function clearAvatar() {
    try {
      if (record?.userId) {
        await userApiService.update([
          { ...record, userId: record.userId, imageUrl: '' } as ApiUser,
        ]);
      }
    } catch {
      // Even if the server rejects (e.g. no `imageUrl` column yet),
      // clear locally so the UI feels responsive.
    }
    dispatch(updateProfile({ avatar: undefined }));
    toast.info('Avatar removed');
  }

  async function handleSave() {
    if (!record) return;
    setTouched({ firstName: true, lastName: true, email: true, phone: true });
    const problem = Object.values(profileErrors).find(Boolean);
    if (problem) {
      toast.error("Fix errors", problem);
      return;
    }
    setSaving(true);
    try {
      const updated: ApiUser = {
        ...record,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
      };
      await userApiService.update([updated]);
      toast.success('Profile updated', 'Your details were saved.');
      // Reflect the new display name + email in the auth slice so the
      // sidebar / header pick them up immediately.
      dispatch(
        updateProfile({
          name: `${updated.firstName} ${updated.lastName}`.trim(),
          email: updated.email,
        }),
      );
      await load();
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  /* Password-change modal — POSTs to `/api/auth/change-password`. The
     `decorateError` interceptor surfaces server validation errors
     (wrong current password, weak new password, rate-limit) directly
     to the toast. Active session stays valid per the backend spec —
     no token rotation. */
  const [pwdOpen, setPwdOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const pwdValid =
    currentPwd.length >= 1 && newPwd.length >= 6 && newPwd === confirmPwd;
  function handleChangePassword() {
    setCurrentPwd('');
    setNewPwd('');
    setConfirmPwd('');
    setPwdOpen(true);
  }
  async function submitPasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (!pwdValid || pwdSubmitting) return;
    setPwdSubmitting(true);
    try {
      await authService.changePassword({
        currentPassword: currentPwd,
        newPassword: newPwd,
      });
      toast.success('Password updated', 'Use the new password next time you sign in.');
      setPwdOpen(false);
    } catch (err) {
      toast.error('Couldn\'t change password', err instanceof Error ? err.message : 'Try again.');
    } finally {
      setPwdSubmitting(false);
    }
  }

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* Sidebar — identity card */}
        <Card className="flex flex-col items-center gap-3 p-5 text-center">
          <div className="relative">
            <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-border bg-surface-card-hover text-3xl font-semibold text-content-secondary">
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="h-full w-full object-cover" />
              ) : (
                <span>{initial}</span>
              )}
            </div>
            <button
              type="button"
              onClick={pickAvatar}
              aria-label="Change avatar"
              className="absolute -right-1 -bottom-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface-card text-content-primary shadow hover:bg-surface-card-hover"
            >
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-0.5">
            <p className="text-base font-semibold text-content-primary">
              {firstName} {lastName}
            </p>
            <p className="text-xs text-content-muted">{record?.userName}</p>
            {user?.role && (
              <div className="mt-1 flex items-center justify-center gap-1">
                <ShieldCheck className="h-3 w-3 text-primary" />
                <Badge variant="info">{user.role}</Badge>
              </div>
            )}
          </div>
          <div className="flex w-full justify-center gap-2 border-t border-border pt-3">
            <Button size="sm" variant="outline" onClick={pickAvatar}>
              <Camera className="h-3.5 w-3.5" /> Upload photo
            </Button>
            {user?.avatar && (
              <button
                type="button"
                onClick={clearAvatar}
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-content-secondary hover:bg-surface-card-hover"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            )}
          </div>
          <p className="text-[11px] text-content-muted">
            Avatar is stored locally — max {Math.round(MAX_AVATAR_BYTES / 1024)} KB.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onAvatarFile}
            hidden
          />
        </Card>

        {/* Editable details */}
        <div className="grid gap-4">
          <Card className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-content-primary">
                Personal information
              </h2>
              <Button variant="outline" size="sm" onClick={load} disabled={loading || saving}>
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
              </Button>
            </div>
            {loading ? (
              <div className="py-8 text-center text-sm text-content-muted">Loading…</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={markP("firstName")}
                  error={profErr("firstName")}
                  placeholder="Jane"
                />
                <Input
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={markP("lastName")}
                  error={profErr("lastName")}
                  placeholder="Doe"
                />
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={markP("email")}
                  error={profErr("email")}
                  placeholder="you@company.com"
                />
                <Input
                  label="Phone"
                  value={phone}
                  /* Strip non-digits at input — same as web profile.
                     Bug ref: AI Agent Issue #2. */
                  onChange={(e) => setPhone(e.target.value.replace(/\D+/g, '').slice(0, 15))}
                  inputMode="numeric"
                  pattern="\d{10,15}"
                  maxLength={15}
                  placeholder="10-digit mobile"
                  onBlur={markP("phone")}
                  error={profErr("phone")}
                />
                <Input
                  label="Username"
                  value={record?.userName ?? ''}
                  disabled
                  hint="Set during user creation — contact an admin to change."
                />
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Status</span>
                  <div className="flex h-10 items-center">
                    <StatusBadge
                      status={
                        record?.status === 1 || record?.emailVerified ? 'active' : 'inactive'
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
              <Button onClick={handleSave} disabled={saving || loading || !profileValid}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold text-content-primary">Security</h2>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface-card-hover p-3">
              <div>
                <p className="text-sm font-medium text-content-primary">Password</p>
                <p className="text-xs text-content-muted">
                  Last changed: <span className="font-mono">unknown</span>
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleChangePassword}>
                <KeyRound className="h-3.5 w-3.5" /> Change password
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Change-password modal — wired to `authService.changePassword`.
          When the backend endpoint isn't live, the `decorateError`
          interceptor surfaces the actual server message on the toast. */}
      <Modal
        open={pwdOpen}
        onClose={() => (pwdSubmitting ? undefined : setPwdOpen(false))}
        title="Change password"
        width={420}
        footer={
          <>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdSubmitting}>
              Cancel
            </Button>
            <Button onClick={submitPasswordChange} disabled={!pwdValid || pwdSubmitting}>
              {pwdSubmitting ? 'Updating…' : 'Update password'}
            </Button>
          </>
        }
      >
        <form onSubmit={submitPasswordChange} className="grid gap-3">
          <Input
            label="Current password *"
            type="password"
            autoComplete="current-password"
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            required
          />
          <Input
            label="New password *"
            type="password"
            autoComplete="new-password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            required
            hint="Minimum 6 characters."
          />
          <Input
            label="Confirm new password *"
            type="password"
            autoComplete="new-password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            required
            error={confirmPwd && confirmPwd !== newPwd ? 'Passwords don\'t match' : undefined}
          />
          <p className="text-[11px] text-content-muted">
            Your active session stays signed in — no need to log in again.
          </p>
        </form>
      </Modal>
    </div>
  );
}
